const requestIdleCallback = typeof window.requestIdleCallback === "function" ? window.requestIdleCallback : setTimeout;
const cancelIdleCallback = typeof window.cancelIdleCallback === "function" ? window.cancelIdleCallback : clearTimeout;
function debounce(fn, wait) {
  let timeout;
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return (
    /** @type {T & { cancel(): void }} */
    debounced
  );
}
function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
  const animationPromises = animations.reduce(
    (acc, animation) => {
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }
      return acc;
    },
    /** @type {Promise<Animation>[]} */
    []
  );
  return Promise.allSettled(animationPromises).then(callback);
}
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
function prefersReducedMotion() {
  return reducedMotion.matches;
}
function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
function throttle(fn, ms) {
  let lastCall = 0;
  return function(...args) {
    const now = Date.now();
    if (now - lastCall >= ms) {
      lastCall = now;
      return fn.apply(this, args);
    }
  };
}
function formatWithDelimiters(cents, precision, thousands, decimal) {
  const [int, frac] = (cents / 100).toFixed(precision).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return frac ? grouped + decimal + frac : grouped;
}
const MONEY_FORMAT_TOKENS = {
  amount: { precision: 2, thousands: ",", decimal: "." },
  amount_no_decimals: { precision: 0, thousands: ",", decimal: "." },
  amount_with_comma_separator: { precision: 2, thousands: ".", decimal: "," },
  amount_no_decimals_with_comma_separator: {
    precision: 0,
    thousands: ".",
    decimal: ","
  },
  amount_with_apostrophe_separator: {
    precision: 2,
    thousands: "'",
    decimal: "."
  },
  amount_no_decimals_with_space_separator: {
    precision: 0,
    thousands: " ",
    decimal: "."
  },
  amount_with_space_separator: { precision: 2, thousands: " ", decimal: "," }
};
function deriveSeparators(sample) {
  if (typeof sample !== "string") {
    return null;
  }
  const run = sample.match(/\d[\d.,'\u00a0\u202f ]*\d/)?.[0];
  if (!run) {
    return null;
  }
  const decimal = run.match(/([.,'\u00a0\u202f ])\d{2}$/)?.[1] ?? null;
  const thousands = [...new Set(run.replace(/\d/g, ""))].find((char) => char !== decimal);
  return thousands ? { precision: decimal ? 2 : 0, thousands, decimal } : null;
}
function renderMoneyFormat(cents, format, sample) {
  if (typeof format !== "string") {
    return null;
  }
  const derived = deriveSeparators(sample);
  let matched = false;
  const out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, (token, name) => {
    const spec = MONEY_FORMAT_TOKENS[name];
    if (!spec) {
      return token;
    }
    matched = true;
    const style = derived ?? spec;
    return formatWithDelimiters(
      cents,
      style.precision,
      style.thousands,
      style.decimal ?? spec.decimal
    );
  });
  return matched ? out : null;
}
function formatMoney(cents, options = {}) {
  if (typeof cents !== "number" || isNaN(cents)) {
    return "";
  }
  const { locale = "de-DE", currency = "EUR" } = options;
  const format = options.format ?? window.Theme?.moneyFormat;
  const sample = options.sample ?? (options.format === void 0 ? window.Theme?.moneyFormatSample : void 0);
  if (currency !== "EUR") {
    const rendered = renderMoneyFormat(cents, format, sample);
    if (rendered !== null) {
      return rendered;
    }
  }
  const effLocale = currency === "CHF" ? "en-US" : locale;
  const value = cents / 100;
  let out = new Intl.NumberFormat(effLocale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
  if (currency === "EUR" && effLocale.startsWith("de")) {
    out = out.replace(/[\u00A0\u202F]/g, "");
  }
  return out;
}
function defineCustomElement(name, constructor) {
  if (!customElements.get(name)) {
    customElements.define(name, constructor);
  }
}
function updateGalleryMedia(gallery, start, end) {
  const selectors = [
    ".carousel-slide[data-media-position]",
    ".carousel-thumbnail[data-media-position]",
    ".product-media-grid-item[data-media-position]"
  ];
  for (const selector of selectors) {
    const elements = Array.from(gallery.querySelectorAll(selector));
    elements.forEach((el) => {
      const pos = parseInt(el.dataset.mediaPosition, 10);
      if (Number.isNaN(pos)) {
        el.classList.remove("media-hidden");
        return;
      }
      el.classList.toggle("media-hidden", !(pos >= start && pos < end));
    });
    const extra = elements.find((el) => el.dataset.mediaPosition === "ablyft-media");
    if (extra) {
      const target = elements.find((el) => parseInt(el.dataset.mediaPosition, 10) === start);
      if (target && extra.previousElementSibling !== target) {
        target.insertAdjacentElement("afterend", extra);
      }
    }
  }
  const gridItems = Array.from(
    gallery.querySelectorAll(".product-media-grid-item[data-media-position]")
  );
  gridItems.forEach((item) => item.classList.remove("col-span-2"));
  const hero = gridItems.find((item) => {
    const pos = parseInt(item.dataset.mediaPosition, 10);
    return !Number.isNaN(pos) && !item.classList.contains("media-hidden");
  });
  if (hero) {
    hero.classList.add("col-span-2");
  }
  const embla = gallery.querySelector("carousel-embla");
  if (embla?.embla) {
    if (typeof embla.embla.reInit === "function") {
      embla.embla.reInit({ startSnap: 0 });
    }
    if (typeof embla.embla.goTo === "function") {
      embla.embla.goTo(0, true);
    }
  }
}
export {
  debounce as a,
  cancelIdleCallback as c,
  defineCustomElement as d,
  escapeHtml as e,
  formatMoney as f,
  onAnimationEnd as o,
  prefersReducedMotion as p,
  requestIdleCallback as r,
  throttle as t,
  updateGalleryMedia as u
};
